import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'i18n'
import { Image } from '@unpic/react'
import { format } from 'date-fns'
import { Turnstile } from '@marsidev/react-turnstile'
import { CircularProgress } from '@mui/material'
import * as styles from './IncommOfferBarcodeResult.module.scss'
import { IncommRetailerInstructions } from './IncommRetailerInstructions'
import { getBannerStoreById } from '../constants/banner-stores'
import { WalletButton } from 'components/WalletButton'
import { sendBarcodeEmail } from 'services/incomm/barcode'
import { useDeviceInfo } from 'hooks/useDeviceInfo'
import analytics from 'global/AnalyticsData'
import { getBarcodeImageUrl } from '../utils/helpers'

type EmailStatus = 'idle' | 'loading' | 'success' | 'error'
type WalletStatus = 'idle' | 'loading' | 'error'

interface IncommOfferBarcodeResultProps {
  barcodeAccessToken: string
  barcodeText: string
  offerEnd?: string
  selectedStoreId: string
  email?: string
  offerId?: string | number
  stepId?: string
  step?: any
  autoSendEmail?: boolean | string | number
  autoSendBarcodeEmail?: boolean | string | number
}

const normalizeBooleanFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase()
    return normalizedValue === 'true' || normalizedValue === '1'
  }

  return false
}

export const IncommOfferBarcodeResult = ({
  barcodeAccessToken,
  barcodeText,
  selectedStoreId,
  offerEnd,
  email,
  offerId,
  stepId,
  step,
  autoSendEmail = false,
  autoSendBarcodeEmail = false,
}: IncommOfferBarcodeResultProps) => {
  const { t } = useTranslation(['incommOffer', 'common'])
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [walletStatus, setWalletStatus] = useState<WalletStatus>('idle')
  const [walletError, setWalletError] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [selectedBannerStore, setSelectedBannerStore] = useState<any>(null)
  const turnstileRef = useRef<any>(null)
  const deviceType = useDeviceInfo()

  // Load banner store data asynchronously
  useEffect(() => {
    const loadBannerStore = async () => {
      if (selectedStoreId) {
        const store = await getBannerStoreById(selectedStoreId)
        setSelectedBannerStore(store)
      }
    }
    loadBannerStore()
  }, [selectedStoreId])

  const hideBarcodeImage = selectedBannerStore?.hideBarcodeImage || false
  const isQRCode = selectedBannerStore?.barcodeFormat === 'QR_CODE'

  // Determine wallet type based on device
  const walletType = deviceType === 'ios' ? 'apple' : 'google'

  // Construct the barcode image URL directly using the access token with format parameter
  const barcodeImageUrl = getBarcodeImageUrl(
    barcodeAccessToken,
    selectedBannerStore?.barcodeFormat,
  )


  const formattedExpiration = offerEnd
    ? (() => {
        try {
          let parsedDate: Date
          
          // Handle array format [year, month, day, hour, minute, second]
          if (Array.isArray(offerEnd)) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = offerEnd
            parsedDate = new Date(year, month - 1, day, hour, minute, second)
          } else {
            // Handle string format
            parsedDate = new Date(offerEnd)
          }
          
          return isNaN(parsedDate.getTime()) ? '' : format(parsedDate, 'MM/dd/yyyy')
        } catch (error) {
          console.error('Invalid offerEnd date:', offerEnd, error)
          return ''
        }
      })()
    : ''

  // Only show email/wallet buttons if we have required data
  const shouldAutoSendEmail =
    normalizeBooleanFlag(autoSendEmail) ||
    normalizeBooleanFlag(autoSendBarcodeEmail)
  const canSendEmail =
    !shouldAutoSendEmail && email && offerId && barcodeAccessToken
  const canAddToWallet = email && offerId && barcodeAccessToken

  // Turnstile event handlers
  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token)
  }, [])

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null)
    // Auto-retry after error
    setTimeout(() => {
      turnstileRef.current?.reset()
    }, 1000)
  }, [])

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null)
    // Auto-refresh on expiration
    turnstileRef.current?.reset()
  }, [])

  // Get a fresh turnstile token, with retry logic
  const getFreshTurnstileToken = async (): Promise<string | null> => {
    // If we have a stored token, use it
    if (turnstileToken) {
      return turnstileToken
    }

    // Try to get token from ref
    const existingToken = turnstileRef.current?.getResponse()
    if (existingToken) {
      return existingToken
    }

    // Reset and wait for new token
    turnstileRef.current?.reset()

    // Wait for new token (max 10 seconds with better polling)
    const maxWaitTime = 10000
    const pollInterval = 200
    const startTime = Date.now()

    while (Date.now() - startTime < maxWaitTime) {
      // Check stored token first (set by onSuccess callback)
      if (turnstileToken) {
        return turnstileToken
      }

      // Check ref response
      const newToken = turnstileRef.current?.getResponse()
      if (newToken) {
        return newToken
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval))
    }

    return null
  }

  const handleSendEmail = async () => {
    if (!canSendEmail || !selectedBannerStore) return

    setEmailStatus('loading')
    setEmailError(null)

    try {
      const token = await getFreshTurnstileToken()

      if (!token) {
        setEmailError(t('incommOffer:barcode.emailError.verificationFailed'))
        setEmailStatus('error')
        // Reset turnstile for retry
        turnstileRef.current?.reset()
        setTurnstileToken(null)
        return
      }

      // Clear stored token (tokens are single-use)
      setTurnstileToken(null)

      // Extract data from the already displayed banner store info
      const retailerStore = selectedBannerStore.name
      const retailerStoreCashierInformation =
        selectedBannerStore.cashierInstructions.description
      const retailerStoreIconUrl = selectedBannerStore.logoUrl
      const barcodeType = selectedBannerStore.barcodeFormat

      await sendBarcodeEmail({
        email,
        offerId: typeof offerId === 'string' ? Number(offerId) : offerId || 0,
        barcodeAccessToken,
        barcodeType,
        retailerStore,
        retailerStoreCashierInformation,
        retailerStoreIconUrl,
        turnstileToken: token,
        stepId,
      })

      setEmailStatus('success')

      // Track successful email send
      if (step) {
        analytics.stepEvent('step_interaction_incomm_email_sent', step, {
          result: 'email_sent_success',
          context: 'barcode_email',
          context_id: selectedStoreId,
          result_id: email,
        })
      }

      // Reset for potential future use
      setTimeout(() => {
        setEmailStatus('idle')
        turnstileRef.current?.reset()
      }, 3000)
    } catch (error: any) {
      console.error('Failed to send barcode email:', error)

      const errorStatus = error?.status
      const errorDetail = error?.detail || ''
      let errorMessage = t('incommOffer:barcode.emailError.generic')

      if (errorStatus === 400) {
        // Check if it's a turnstile validation error
        if (
          errorDetail.toLowerCase().includes('turnstile') ||
          errorDetail.toLowerCase().includes('token')
        ) {
          errorMessage = t('incommOffer:barcode.emailError.verificationExpired')
        } else {
          errorMessage = t('incommOffer:barcode.emailError.invalidRequest')
        }
      } else if (errorStatus === 403) {
        errorMessage = t('incommOffer:barcode.emailError.accessDenied')
      } else if (errorStatus === 404) {
        errorMessage = t('incommOffer:barcode.emailError.serviceUnavailable')
      } else if (errorStatus === 500) {
        errorMessage = t('incommOffer:barcode.emailError.serverError')
      }

      setEmailError(errorMessage)
      setEmailStatus('error')

      // Track email send failure
      if (step) {
        analytics.stepEvent('step_interaction_incomm_email_failed', step, {
          result: 'email_sent_failed',
          context: 'barcode_email_error',
          context_id: selectedStoreId,
          result_id: errorStatus ? String(errorStatus) : 'unknown_error',
        })
      }

      // Reset turnstile for retry
      turnstileRef.current?.reset()
      setTurnstileToken(null)

      // Allow retry after showing error
      setTimeout(() => {
        setEmailStatus('idle')
        setEmailError(null)
      }, 5000)
    }
  }

  // Single-CTA wallet flow:
  // The WalletButton is rendered as a real <a href="..."> and the backend should respond
  // with a 302/303 redirect to the wallet provider. This avoids popup-blocker issues and
  // lets the user keep this page open in the original tab.
  //
  // Backend contract (recommended):
  // - GET /incomm/barcode/add-to-wallet?... -> 302/303 Location: <wallet-url>
  // - Prefer minimal query params (avoid PII/large strings); use IDs / tokens.
  const addToWalletRedirectHref =
    canAddToWallet && process.env.DAILYBREAK_API_URL && selectedBannerStore
      ? `${
          process.env.DAILYBREAK_API_URL
        }/incomm/barcode/add-to-wallet?email=${encodeURIComponent(
          email!,
        )}&offerId=${encodeURIComponent(
          String(offerId || ''),
        )}&barcodeAccessToken=${encodeURIComponent(
          barcodeAccessToken,
        )}&barcodeType=${encodeURIComponent(
          selectedBannerStore.barcodeFormat,
        )}&walletType=${encodeURIComponent(
          walletType,
        )}&storeId=${encodeURIComponent(selectedStoreId)}${
          stepId ? `&stepId=${encodeURIComponent(stepId)}` : ''
        }`
      : null

  const getEmailButtonContent = () => {
    switch (emailStatus) {
      case 'loading':
        return <CircularProgress color="inherit" size={18} />
      case 'success':
        return t('incommOffer:barcode.emailSent')
      case 'error':
        return t('common:retry')
      default:
        return t('incommOffer:barcode.saveViaEmail')
    }
  }

  return (
    <div className={styles.barcodeResultContainer}>
      <div className={styles.barcodeSection}>
        {!hideBarcodeImage && barcodeImageUrl && (
          <Image
            src={barcodeImageUrl}
            height={isQRCode ? 152 : 76}
            width={isQRCode ? 152 : 500}
            alt={t('incommOffer:barcode.imageAlt')}
            className={`${styles.barcodeImage} ${
              isQRCode ? styles.qrCode : ''
            }`}
          />
        )}
        <div data-background={hideBarcodeImage} className={styles.barcodeText}>
          {barcodeText}
        </div>
        {formattedExpiration && (
          <div className={styles.barcodeTextExpiration}>
            {t('incommOffer:barcode.expiresAt', { date: formattedExpiration })}
          </div>
        )}
      </div>

      {selectedBannerStore && (
        <IncommRetailerInstructions
          retailerInstructions={selectedBannerStore}
        />
      )}

      <div className={styles.barcodeButtonContainer}>
        <div className={styles.buttonRow}>
          {canAddToWallet && (
            <WalletButton
              href={addToWalletRedirectHref || undefined}
              target="_blank"
              rel="noopener noreferrer"
              isLoading={walletStatus === 'loading'}
              disabled={walletStatus === 'loading' || !addToWalletRedirectHref}
              onClick={() => {
                // Track wallet button click
                if (step) {
                  analytics.stepEvent('step_interaction_incomm_wallet_click', step, {
                    result: 'wallet_button_clicked',
                    context: 'add_to_wallet',
                    context_id: selectedStoreId,
                    result_id: walletType,
                  })
                }

                // Keep existing status semantics: show an error if FE is misconfigured
                // (e.g. DAILYBREAK_API_URL missing).
                if (!addToWalletRedirectHref) {
                  setWalletError(t('incommOffer:barcode.walletLinkError'))
                  setWalletStatus('error')
                }
              }}
            />
          )}
          {canSendEmail && (
            <button
              className={`${styles.barcodeButton} ${
                emailStatus === 'success' ? styles.barcodeButtonSuccess : ''
              } ${emailStatus === 'error' ? styles.barcodeButtonError : ''}`}
              onClick={handleSendEmail}
              disabled={emailStatus === 'loading'}
            >
              {getEmailButtonContent()}
            </button>
          )}
        </div>
        {walletError && (
          <p className={styles.emailErrorMessage}>{walletError}</p>
        )}
        {emailError && <p className={styles.emailErrorMessage}>{emailError}</p>}
      </div>

      {/* Invisible Turnstile widget for email and wallet security */}
      {(canSendEmail || canAddToWallet) && (
        <div className={styles.turnstileContainer}>
          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.INVISIBLE_TURNSTILE_SITE_KEY || ''}
            options={{
              size: 'invisible',
              refreshExpired: 'auto',
              retry: 'auto',
              retryInterval: 3000,
            }}
            onSuccess={handleTurnstileSuccess}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpire}
          />
        </div>
      )}
    </div>
  )
}
